import { grayColor } from 'assets/jss/material-dashboard-pro-react';
import {
  Field, FieldProps, Form, Formik,
} from 'formik';
import passwordComplexity from 'joi-password-complexity';
import React from 'react';
import { useLocation } from 'react-router-dom';
import url from 'url';
import * as Yup from 'yup';

import {
  ButtonBase, Grid, makeStyles, useTheme,
} from '@material-ui/core';
import InfoOutlinedIcon from '@material-ui/icons/InfoOutlined';
import LibraryAddCheckRoundedIcon from '@material-ui/icons/LibraryAddCheckRounded';

import Button from 'creative-components/CustomButtons/Button';
import CustomInputDashboard from 'creative-components/CustomInput/CustomInputDashboard';

import ActionModal from 'components/ActionModal/ActionModal';
import { useAuthDataContext } from 'components/AuthDataProvider/AuthDataProvider';
import FormikOnError from 'components/FormikOnError/FormikOnError';
import { useLoadingIndicatorContext } from 'components/LoadingIndicator/LoadingIndicatorProvider';
import PasswordValidationInfo from 'components/PasswordValidationInfo';
import PasswordVisibility from 'components/PasswordVisibility/PasswordVisibility';
import AddPaymentMethodButton from 'components/PaymentMethods/AddPaymentMethodButton';
import PaymentMethodSelector from 'components/PaymentMethods/PaymentMethodSelector';
import { useUserInfoContext } from 'components/UserInfoProvider/UserInfoProvider';

import analyticsClient from 'utils/analytics';
import { authenticate, submitInterestApplication } from 'utils/api';
import {
  agentAdvicePageUrl,
  maxPasswordLength, nameRegExp, passwordComplexityOptions, passwordValidationChecks, phoneRegExp, showAPIErrorAlert,
} from 'utils/lib';

const useStyles = makeStyles((theme) => ({
  card: {
    height: '250px',
    display: 'flex',
    flexDirection: 'column',
  },
  cardContent: {
    flexGrow: 1,
  },
  cardActions: {
    justifyContent: 'center',
  },
  field: {
    maxWidth: '400px',
  },
  inputAdornmentIcon: {
    color: grayColor[6],
  },
  confirmPopUp: {
    [theme.breakpoints.up('md')]: {
      minWidth: '500px',
    },
  },
  startButton: {
    float: 'right',
  },
  cancelButton: {
    backgroundColor: `${theme.palette.grayScale6.main} !important`,
  },
}));

interface FormValues {
  firstName: string;
  lastName: string;
  phoneNumber: string;
  email: string;
  password: string;
  passwordConfirmation: string;
}

type Props = {
  submitButtonText: string;
  handleNext: () => void;
  onLaunch: () => Promise<void>;
  paymentMethodId: string;
  setPaymentMethodId: React.Dispatch<React.SetStateAction<string>>;
};

export default ({
  submitButtonText, paymentMethodId, setPaymentMethodId, handleNext, onLaunch,
}: Props) => {
  const theme = useTheme();
  const classes = useStyles();
  const location = useLocation();
  const { showLoadingIndicatorModal, hideLoadingIndicatorModal } = useLoadingIndicatorContext() as any;
  const { onLogin, user } = useAuthDataContext() as any;
  const { userInfo, reloadUserInfo } = useUserInfoContext();

  const [showPassword, setShowPassword] = React.useState(false);
  const [showBillingModal, setShowBillingModal] = React.useState(false);

  const toggleShowPassword = () => setShowPassword(!showPassword);

  React.useEffect(() => {
    const analyticsCampaignEventType = 'Full Farm';

    // Track GTM event
    analyticsClient.track('simple_campaign_creation_started', {
      campaign_event_type: analyticsCampaignEventType,
    });
  }, []);

  React.useEffect(() => {
    if (user) reloadUserInfo();
  }, [user]);

  React.useEffect(() => {
    if (userInfo && !paymentMethodId) {
      setShowBillingModal(true);
      hideLoadingIndicatorModal();
    }
  }, [userInfo, paymentMethodId]);

  return (
    <Formik
      initialValues={{
        firstName: user?.firstName ?? '',
        lastName: user?.lastName ?? '',
        email: userInfo?.email ?? '',
        phoneNumber: userInfo?.phoneNumber ?? '',
        password: '',
        passwordConfirmation: '',
      } as FormValues}
      validateOnChange
      onSubmit={async (values) => {
        const {
          firstName,
          lastName,
          phoneNumber,
          email,
          password,
        } = values;

        try {
          showLoadingIndicatorModal();

          if (!userInfo) {
            // Register the user

            let referral;
            if (location.pathname.includes(agentAdvicePageUrl)) {
              // Set AgentAdvice referral code
              referral = 'agentadvice-hp2s-created';
            }

            const { passwordResetUrl } = await submitInterestApplication(firstName, lastName, email, phoneNumber, '', true, referral);

            const queryParams = url.parse(passwordResetUrl, true);
            const passwordResetToken = queryParams.query.token as string;

            const { token, expiresInDays } = await authenticate(email, password, passwordResetToken);

            // Call the login function on the auth context
            onLogin(token, expiresInDays, true, email, false);

            // Keep the loading indicator modal up until the payment modal opens up
          } else {
            // Make sure they have a payment method on file
            if (!paymentMethodId) {
              setShowBillingModal(true);
              hideLoadingIndicatorModal();
              return;
            }
            setShowBillingModal(false);

            await onLaunch();
            await new Promise((resolve) => setTimeout(resolve, 500)); // To allow the loading indicator to show a bit

            handleNext();

            hideLoadingIndicatorModal();
          }
        } catch (err) {
          console.error('error', err);
          showAPIErrorAlert(err);

          hideLoadingIndicatorModal();
        }
      }}
      validationSchema={Yup.object().shape({
        firstName: Yup.string().required().matches(nameRegExp),
        lastName: Yup.string().required().matches(nameRegExp),
        phoneNumber: Yup.string().required().matches(phoneRegExp, 'Phone number is not valid'),
        email: Yup.string().email().required(),
        password: Yup.string().max(maxPasswordLength)
          .test(
            'complexity',
            'Password must be between 8 and 24 characters',
            (value) => !passwordComplexity(passwordComplexityOptions).validate(value).error,
          )
          .test(
            'user-dependent-required',
            'Field is required',
            (value) => !!user || !!value,
          ),
        passwordConfirmation: Yup.string().oneOf([Yup.ref('password')], 'Passwords must match')
          .test(
            'user-dependent-required',
            'Field is required',
            (value) => !!user || !!value,
          ),
      }).required()}
    >
      {({
        dirty, isValid, handleSubmit, isSubmitting, setFieldValue, values,
      }) => {
        const passwordValidationInfos = [] as React.ReactNode[];
        const passwordValidation = passwordComplexity(passwordComplexityOptions).validate(values.password);

        const passwordErrors = passwordValidation.error ? passwordValidation.error.details.map(({ type }) => type) : [];

        passwordValidationChecks.forEach(({ description, errorName }: { description: string, errorName: string }) => {
          passwordValidationInfos.push(<PasswordValidationInfo
            key={errorName}
            description={description}
            valid={values.password.length > 0 && !passwordErrors.includes(errorName)}
          />);
        });

        return (
          <Form>
            <FormikOnError>
              {showBillingModal && (
                <ActionModal
                  maxWidth="sm"
                  backgroundColor={theme.palette.offWhite.main}
                  textColor={theme.palette.darkGray.main}
                  icon={<LibraryAddCheckRoundedIcon />}
                  onClose={() => setShowBillingModal(false)}
                  title="Choose Payment Method"
                  message={(
                    <div className={classes.confirmPopUp}>
                      <PaymentMethodSelector
                        inputProps={{
                          value: paymentMethodId,
                          onChange: (event: any) => setPaymentMethodId(event.target.value),
                        }}
                      />
                      <br />
                      <AddPaymentMethodButton center />
                      <br />
                    </div>
                  )}
                  buttons={[
                    <ButtonBase onClick={() => setShowBillingModal(false)} className={classes.cancelButton}>Go Back</ButtonBase>,
                    <ButtonBase onClick={() => handleSubmit()}>Start</ButtonBase>,
                  ]}
                />
              )}

              <Grid container justifyContent="flex-start">
                <Grid item xs={12} md={6}>
                  <Field
                    name="firstName"
                  >
                    {({ field, meta }: FieldProps) => (
                      <CustomInputDashboard
                        className={classes.field}
                        labelText="First Name"
                        inputProps={{
                          ...field,
                          disabled: !!user, // User was already registered
                          autoComplete: 'given-name',
                          placeholder: 'Enter name',
                          maxLength: 50,
                        }}
                        error={!!meta.touched && !!meta.error}
                      />
                    )}
                  </Field>
                </Grid>
                <Grid item xs={12} md={6}>
                  <Field
                    name="lastName"
                  >
                    {({ field, meta }: FieldProps) => (
                      <CustomInputDashboard
                        className={classes.field}
                        labelText="Last Name"
                        inputProps={{
                          ...field,
                          disabled: !!user, // User was already registered
                          autoComplete: 'family-name',
                          placeholder: 'Enter name',
                          maxLength: 50,
                        }}
                        error={!!meta.touched && !!meta.error}
                      />
                    )}
                  </Field>
                </Grid>
                <Grid item xs={12} md={6}>
                  <Field
                    name="email"
                  >
                    {({ field, meta }: FieldProps) => (
                      <CustomInputDashboard
                        className={classes.field}
                        labelText="Email Address"
                        inputProps={{
                          ...field,
                          disabled: !!user, // User was already registered
                          autoComplete: 'email',
                          placeholder: 'Enter email address',
                        }}
                        error={!!meta.touched && !!meta.error}
                      />
                    )}
                  </Field>
                </Grid>

                {!user && (
                  <>
                    <Grid item xs={12} md={6}>
                      <Field
                        name="phoneNumber"
                      >
                        {({ field, meta }: FieldProps) => (
                          <CustomInputDashboard
                            className={classes.field}
                            labelText="Phone Number"
                            inputProps={{
                              ...field,
                              disabled: !!user, // User was already registered
                              type: 'tel',
                              autoComplete: 'tel',
                              endAdornment: <InfoOutlinedIcon />,
                              placeholder: 'Enter phone number',
                            }}
                            mask={['(', /[1-9]/, /\d/, /\d/, ')', ' ', /\d/, /\d/, /\d/, '-', /\d/, /\d/, /\d/, /\d/]}
                            error={!!meta.touched && !!meta.error}
                            tooltip="Used for internal communications, i.e. account and campaign updates"
                          />
                        )}
                      </Field>
                    </Grid>

                    <Grid item xs={12} md={6}>
                      <Field
                        name="password"
                      >
                        {({ field, meta }: FieldProps) => (
                          <CustomInputDashboard
                            labelText="Password"
                            className={classes.field}
                            inputProps={{
                              ...field,
                              disabled: !!user, // User was already registered
                              type: showPassword ? 'text' : 'password',
                              endAdornment: (
                                <PasswordVisibility
                                  showPassword={showPassword}
                                  toggleShowPassword={toggleShowPassword}
                                  className={classes.inputAdornmentIcon}
                                />
                              ),
                              onChange: (e: any) => setFieldValue(field.name, e.target.value.substring(0, 16), true),
                              placeholder: 'Enter password',
                            }}
                            error={!!meta.touched && !!meta.error}
                          />
                        )}
                      </Field>
                    </Grid>
                    <Grid item xs={12} md={6}>
                      <Field
                        name="passwordConfirmation"
                      >
                        {({ field, meta }: FieldProps) => (
                          <CustomInputDashboard
                            labelText="Confirm password"
                            className={classes.field}
                            inputProps={{
                              ...field,
                              disabled: !!user, // User was already registered
                              type: showPassword ? 'text' : 'password',
                              onChange: (e: any) => setFieldValue(field.name, e.target.value.substring(0, 16), true),
                              onKeyDown: (event: any) => {
                                if (event.key === 'Enter') {
                                  // Same checks as submit button below
                                  if (isValid && dirty && !isSubmitting) {
                                    handleSubmit();
                                  }
                                }
                              },
                              placeholder: 'Confirm password',
                            }}
                            error={!!meta.touched && !!meta.error}
                          />
                        )}
                      </Field>
                    </Grid>
                    <Grid item xs={12}>
                      {passwordValidationInfos}
                    </Grid>
                  </>
                )}

              </Grid>

              <Button
                round
                color="primary"
                className={classes.startButton}
                disabled={isSubmitting}
                onClick={() => handleSubmit()}
              >
                {submitButtonText}
              </Button>
            </FormikOnError>
          </Form>
        );
      }}
    </Formik>
  );
};
